/**
 * 买家端鉴权上下文（对接真实后端 /api/buyer/login）
 * ---------------------------------------------------------------------------
 * 提供 useBuyerAuth()：
 *   - isAuthenticated: 是否已登录（token 存在即视为已登录）
 *   - user: BuyerUser | null —— 真实买家概要（buyerId/buyerCode/companyName/email/orgId）
 *   - login(email, password): Promise<boolean> —— 调 /api/buyer/login，成功写入真实 JWT + 概要；失败抛错（message 可直显）
 *   - logout(): 清除 localStorage 并复位状态
 * 挂载时从 localStorage 初始化（有 token 即视为已登录），与移动端 RoleContext 范式一致。
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import { post, TOKEN_KEY } from '@/api/request';
import { authApi, type BuyerProfileRequest, type BuyerRegisterRequest, type SendCodeType } from '@/api/authApi';
import { BUYER_CHAT_STORAGE_KEY } from '@/lib/customerChat';
import { loadNavCreds, saveNavCredsUsernameOnly, clearNavCreds } from '@/utils/navCreds';

import { tr } from '@/i18n';
/** localStorage 键名（买家端独立命名空间，避免与 mobile 包冲突） */
const USER_KEY = 'bp_buyer_user';

/** 后端 BuyerLoginVO 结构（仅取前端需要字段） */
interface BuyerLoginVO {
  token: string;
  tokenType: string;
  expiresIn: number;
  buyerId: string;
  buyerCode: string;
  companyName: string;
  email: string;
  orgId: string;
  contactName?: string;
  country?: string;
  phone?: string;
}

/** 当前登录买家用户（真实概要） */
export interface BuyerUser {
  buyerId: string;
  buyerCode: string;
  companyName: string;
  email: string;
  orgId: string;
  contactName?: string;
  country?: string;
  phone?: string;
}

/** 鉴权上下文对外暴露的接口 */
export interface BuyerAuthContextValue {
  /** 是否已登录 */
  isAuthenticated: boolean;
  /** 当前登录用户（未登录为 null） */
  user: BuyerUser | null;
  /** 登录：传入邮箱与密码，成功返回 true，失败抛出异常（message 可直显） */
  login: (email: string, password: string) => Promise<boolean>;
  /** 注册并登录：邮箱 + 验证码 + 密码，成功返回 true，失败抛出异常 */
  register: (req: BuyerRegisterRequest) => Promise<boolean>;
  /** 发送注册/重置验证码到邮箱（lang 跟随买家端语言偏好，决定邮件中英文） */
  sendRegisterCode: (email: string, type?: SendCodeType, lang?: 'zh' | 'en') => Promise<void>;
  /** 完善企业信息：调后端更新并同步本地概要（companyName/contactName/country） */
  updateProfile: (req: BuyerProfileRequest) => Promise<void>;
  /** 登出：清空登录态 */
  logout: () => void;
}

const BuyerAuthContext = createContext<BuyerAuthContextValue | undefined>(undefined);

/** 从 localStorage 读取持久化的登录态；不存在或异常时返回 null */
function readInitialAuth(): { token: string; user: BuyerUser } | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const rawUser = localStorage.getItem(USER_KEY);
    if (!token || !rawUser) return null;
    const user = JSON.parse(rawUser) as BuyerUser;
    // 新结构以 buyerId 作为必填校验；旧 Mock（仅 username）自动作废
    if (!user || typeof user !== 'object' || !user.buyerId) return null;
    return { token, user };
  } catch (err) {
    console.warn('[BuyerAuth] 读取本地登录态失败', err);
    return null;
  }
}

export function BuyerAuthProvider({ children }: { children: React.ReactNode }) {
  // 挂载时从 localStorage 初始化；有 token 即视为已登录
  const initial = readInitialAuth();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(Boolean(initial));
  const [user, setUser] = useState<BuyerUser | null>(initial?.user ?? null);

  const applyLogin = useCallback((vo: BuyerLoginVO) => {
    const profile: BuyerUser = {
      buyerId: vo.buyerId,
      buyerCode: vo.buyerCode,
      companyName: vo.companyName,
      email: vo.email,
      orgId: vo.orgId,
      contactName: vo.contactName,
      country: vo.country,
      phone: vo.phone,
    };
    try {
      localStorage.setItem(TOKEN_KEY, vo.token);
      localStorage.setItem(USER_KEY, JSON.stringify(profile));
    } catch (err) {
      console.warn('[BuyerAuth] 写入本地登录态失败', err);
    }
    setUser(profile);
    setIsAuthenticated(true);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    const e = (email ?? '').trim();
    const p = (password ?? '').trim();
    if (!e || !p) return false;

    // 先清掉可能的旧 token/概要，避免脏状态叠加
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }

    // 调真实后端；apiRequest 在 code!==200 时抛 ApiError（message 即后端文案）
    const vo = await post<BuyerLoginVO>('/buyer/login', { email: e, password: p });
    applyLogin(vo);
    return true;
  }, [applyLogin]);

  const register = useCallback(async (req: BuyerRegisterRequest): Promise<boolean> => {
    if (!req?.email || !req?.code || !req?.password) return false;
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
    const vo = await authApi.register(req);
    applyLogin(vo);
    return true;
  }, [applyLogin]);

  const sendRegisterCode = useCallback(async (email: string, type: SendCodeType = 'REGISTER', lang: 'zh' | 'en' = 'zh') => {
    const e = (email ?? '').trim();
    if (!e) throw new Error(tr('auth.emailRequired'));
    await authApi.sendCode({ email: e, type, lang });
  }, []);

  const updateProfile = useCallback(async (req: BuyerProfileRequest) => {
    await authApi.updateProfile(req);
    // 后端仅更新非空字段，本地同样按非空合并，保持两端一致
    setUser((prev) => {
      if (!prev) return prev;
      const next: BuyerUser = {
        ...prev,
        companyName: req.companyName?.trim() || prev.companyName,
        // 概要同步承载联系人/国家/电话：对话框回显、「我的」页展示；后端同口径仅非空更新
        contactName: req.contactName?.trim() || prev.contactName,
        country: req.country?.trim() || prev.country,
        phone: req.phone?.trim() || prev.phone,
      };
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(next));
      } catch (err) {
        console.warn('[BuyerAuth] 写入本地概要失败', err);
      }
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // 清在线咨询会话凭据：防止同浏览器换账号后读到上一账号的会话（隐私）
      localStorage.removeItem(BUYER_CHAT_STORAGE_KEY);
      // 导航页带入的「记住我」账密降级：保留用户名回显、清除密码（共用设备/换账号不残留）
      const saved = loadNavCreds();
      if (saved?.u) saveNavCredsUsernameOnly(saved.u);
      else clearNavCreds();
    } catch (err) {
      console.warn('[BuyerAuth] 清理本地登录态失败', err);
    }
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <BuyerAuthContext.Provider value={{ isAuthenticated, user, login, register, sendRegisterCode, updateProfile, logout }}>
      {children}
    </BuyerAuthContext.Provider>
  );
}

/** 获取买家鉴权状态与方法（须在 BuyerAuthProvider 内部使用） */
export function useBuyerAuth(): BuyerAuthContextValue {
  const ctx = useContext(BuyerAuthContext);
  if (!ctx) throw new Error('useBuyerAuth 必须在 BuyerAuthProvider 内部使用');
  return ctx;
}

export default BuyerAuthProvider;
